import type { BillingInterval } from './planDetails';

/** Legacy browser-wide key (pre per-account). Migrated away; cleared on read/write/logout. */
const LEGACY_STORAGE_KEY = 'cwy_membership_status_v1';

export type MembershipEnrollmentStatus =
  | 'none'
  | 'pending_checkout'
  | 'active'
  | 'expired';

export interface StoredMembershipStatus {
  status: MembershipEnrollmentStatus;
  /** Owning client — required so status cannot leak across accounts in one browser */
  client_id?: string;
  plan_id?: string;
  plan_name?: string;
  plan_subtitle?: string;
  tier?: 'bronze' | 'silver' | 'gold';
  billing?: BillingInterval;
  service_discount_percent?: number;
  /** Snapshot of perk labels at purchase time */
  perk_labels?: string[];
  updated_at: string;
  activated_at?: string;
  /** Period end for the current billing cycle (local until Stripe sync exists) */
  expires_at?: string;
}

export type MembershipPlanInput = {
  plan_id: string;
  plan_name: string;
  plan_subtitle?: string;
  tier: 'bronze' | 'silver' | 'gold';
  billing: BillingInterval;
  service_discount_percent: number;
  perk_labels?: string[];
};

function storageKeyForClient(clientId: string): string {
  return `${LEGACY_STORAGE_KEY}:${clientId}`;
}

function emptyStatus(): StoredMembershipStatus {
  return { status: 'none', updated_at: new Date().toISOString() };
}

/** Period end from activation time + billing interval. */
export function computeMembershipPeriodEnd(
  activatedAt: Date,
  billing: BillingInterval
): Date {
  const end = new Date(activatedAt.getTime());
  if (billing === 'annual') {
    end.setUTCFullYear(end.getUTCFullYear() + 1);
  } else {
    end.setUTCMonth(end.getUTCMonth() + 1);
  }
  return end;
}

/**
 * Resolve stored status against "now".
 * Active memberships past expires_at become expired (purchase flow can restart).
 */
export function resolveMembershipStatus(
  stored: StoredMembershipStatus,
  now: Date = new Date()
): StoredMembershipStatus {
  if (stored.status !== 'active' || !stored.expires_at) {
    return stored;
  }
  const expiresMs = Date.parse(stored.expires_at);
  if (!Number.isFinite(expiresMs) || expiresMs > now.getTime()) {
    return stored;
  }
  return {
    ...stored,
    status: 'expired',
    updated_at: now.toISOString(),
  };
}

export function isMembershipLive(status: MembershipEnrollmentStatus): boolean {
  return status === 'active';
}

/**
 * Membership hub status is local until a server-side enrollment API exists.
 * It MUST be keyed by client_id — a single browser key made every account look subscribed.
 */
export function readMembershipStatus(
  clientId?: string | null,
  now: Date = new Date()
): StoredMembershipStatus {
  // Drop the old shared key so leftover “active” state cannot leak to other logins.
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }

  if (!clientId) {
    return emptyStatus();
  }

  try {
    const raw = localStorage.getItem(storageKeyForClient(clientId));
    if (!raw) {
      return emptyStatus();
    }
    const parsed = JSON.parse(raw) as StoredMembershipStatus;
    if (parsed.client_id && parsed.client_id !== clientId) {
      return emptyStatus();
    }
    const resolved = resolveMembershipStatus(
      { ...parsed, client_id: clientId },
      now
    );
    // Persist transition to expired so UI stays consistent across reloads.
    if (parsed.status === 'active' && resolved.status === 'expired') {
      writeMembershipStatus(clientId, resolved);
    }
    return resolved;
  } catch {
    return emptyStatus();
  }
}

export function writeMembershipStatus(
  clientId: string,
  next: StoredMembershipStatus
): void {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  localStorage.setItem(
    storageKeyForClient(clientId),
    JSON.stringify({ ...next, client_id: clientId })
  );
  window.dispatchEvent(new Event('cwy-membership-status'));
}

/** Clears one client’s status, and always clears the legacy shared key. */
export function clearMembershipStatus(clientId?: string | null): void {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    if (clientId) {
      localStorage.removeItem(storageKeyForClient(clientId));
    }
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event('cwy-membership-status'));
}

export function savePendingMembership(
  clientId: string,
  input: MembershipPlanInput
): void {
  writeMembershipStatus(clientId, {
    status: 'pending_checkout',
    ...input,
    updated_at: new Date().toISOString(),
  });
}

export function activateMembership(
  clientId: string,
  input: MembershipPlanInput,
  activatedAt: Date = new Date()
): void {
  const activatedIso = activatedAt.toISOString();
  const expiresAt = computeMembershipPeriodEnd(activatedAt, input.billing).toISOString();
  writeMembershipStatus(clientId, {
    status: 'active',
    ...input,
    updated_at: activatedIso,
    activated_at: activatedIso,
    expires_at: expiresAt,
  });
}

/** Force-expire an active (or pending) membership — used after period end / tests. */
export function expireMembership(
  clientId: string,
  now: Date = new Date()
): StoredMembershipStatus {
  const current = readMembershipStatus(clientId, now);
  if (current.status === 'none') {
    return current;
  }
  const expired: StoredMembershipStatus = {
    ...current,
    status: 'expired',
    expires_at: current.expires_at || now.toISOString(),
    updated_at: now.toISOString(),
  };
  writeMembershipStatus(clientId, expired);
  return expired;
}

/** Server enrollment statuses from GET /membership/me */
export type ServerMembershipStatus =
  | 'none'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'expired'
  | 'incomplete';

export type ServerMembershipEnrollment = {
  plan_id: string;
  tier: 'bronze' | 'silver' | 'gold';
  billing: BillingInterval;
  status?: ServerMembershipStatus;
  activated_at?: string | null;
  current_period_end?: string | null;
  service_discount_percent?: number;
};

/**
 * Map GET /membership/me → local hub status.
 * - active / past_due → local active (past_due still shows as live until expired)
 * - expired / cancelled → local expired
 * - none / incomplete → leave local unchanged (webhook lag / mid-checkout)
 */
export function applyServerEnrollmentToLocal(
  clientId: string,
  server: {
    status: ServerMembershipStatus;
    enrollment: ServerMembershipEnrollment | null;
  },
  planMeta?: Partial<MembershipPlanInput> & { name?: string; subtitle?: string }
): StoredMembershipStatus {
  const enroll = server.enrollment;
  if (!enroll || server.status === 'none') {
    return readMembershipStatus(clientId);
  }

  if (server.status === 'active' || server.status === 'past_due') {
    const next: StoredMembershipStatus = {
      status: 'active',
      client_id: clientId,
      plan_id: enroll.plan_id,
      plan_name: planMeta?.plan_name || planMeta?.name || enroll.tier,
      plan_subtitle: planMeta?.plan_subtitle || planMeta?.subtitle,
      tier: enroll.tier,
      billing: enroll.billing,
      service_discount_percent:
        planMeta?.service_discount_percent ?? enroll.service_discount_percent ?? 0,
      perk_labels: planMeta?.perk_labels,
      updated_at: new Date().toISOString(),
      activated_at: enroll.activated_at || undefined,
      expires_at: enroll.current_period_end || undefined,
    };
    writeMembershipStatus(clientId, next);
    return next;
  }

  if (server.status === 'expired' || server.status === 'cancelled') {
    const next: StoredMembershipStatus = {
      status: 'expired',
      client_id: clientId,
      plan_id: enroll.plan_id,
      plan_name: planMeta?.plan_name || planMeta?.name || enroll.tier,
      plan_subtitle: planMeta?.plan_subtitle || planMeta?.subtitle,
      tier: enroll.tier,
      billing: enroll.billing,
      service_discount_percent: planMeta?.service_discount_percent ?? 0,
      perk_labels: planMeta?.perk_labels,
      updated_at: new Date().toISOString(),
      activated_at: enroll.activated_at || undefined,
      expires_at: enroll.current_period_end || undefined,
    };
    writeMembershipStatus(clientId, next);
    return next;
  }

  // incomplete — do not clobber optimistic pending/active local state
  return readMembershipStatus(clientId);
}
